import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

const UIParamsConfig = React.memo(({ value, onChange, disabled = false }) => {
  const [uiParams, setUiParams] = useState({
    lmr: [
      {
        key: "faq",
        title: "LIST",
        source: "Question And Answers",
        visible: true
      },
      {
        key: "pdf",
        title: "PDFs",
        source: "Question And Answers",
        visible: true
      },
      {
        key: "exam-qp",
        title: "PYQS",
        source: "Exam QP",
        visible: true
      },
      {
        key: "videos",
        title: "Videos",
        source: "Videos",
        visible: false
      },
      {
        "key": "tips-tricks",
        "title": "Tips & Tricks",
        "source": "Tips & Tricks",
        "visible": false
      }
    ],
    topic_notes: [
      {
        key: "syllabus",
        title: "Syllabus",
        source: "Syllabus",
        visible: true
      },
      {
        key: "topic-notes",
        title: "Notes",
        source: "Notes",
        visible: true
      },
      {
        key: "internal-assessment",
        title: "Internal Assessments",
        source: "Internal Assessment",
        visible: true
      }
    ],
    lmrAvailable: true,
    topicNotesAvailable: true
  });

  const [expandedSections, setExpandedSections] = useState({
    lmr: true,
    topic_notes: true
  });

  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ key: '', title: '', source: '', visible: true });

  // Initialize with provided value or default
  useEffect(() => {
    if (value && typeof value === 'object') {
      setUiParams(value);
    } else if (value && typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        setUiParams(parsed);
      } catch (e) {
        console.warn('Invalid JSON in UIParams:', e);
      }
    }
  }, [value]);

  // Notify parent of changes with debouncing
  useEffect(() => {
    if (onChange) {
      const timeoutId = setTimeout(() => {
        onChange(uiParams);
      }, 100); // Debounce by 100ms
      
      return () => clearTimeout(timeoutId);
    }
  }, [uiParams, onChange]);

  const handleSectionToggle = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const handleItemVisibilityChange = useCallback((section, index, visible) => {
    setUiParams(prev => ({
      ...prev,
      [section]: (prev[section] || []).map((item, i) => 
        i === index ? { ...item, visible } : item
      )
    }));
  }, []);

  const handleItemEdit = useCallback((section, index) => {
    setEditingItem({ section, index });
    setNewItem({ ...uiParams[section][index] });
  }, [uiParams]);

  const handleItemSave = useCallback(() => {
    if (!editingItem || !newItem.key || !newItem.title || !newItem.source) return;

    setUiParams(prev => ({
      ...prev,
      [editingItem.section]: (prev[editingItem.section] || []).map((item, i) => 
        i === editingItem.index ? { ...newItem } : item
      )
    }));

    setEditingItem(null);
    setNewItem({ key: '', title: '', source: '', visible: true });
  }, [editingItem, newItem]);

  const handleItemDelete = useCallback((section, index) => {
    setUiParams(prev => ({
      ...prev,
      [section]: (prev[section] || []).filter((_, i) => i !== index)
    }));
  }, []);

  const handleAddItem = useCallback((section) => {
    if (!newItem.key || !newItem.title || !newItem.source) return;

    setUiParams(prev => ({
      ...prev,
      [section]: [...(prev[section] || []), { ...newItem }]
    }));

    setNewItem({ key: '', title: '', source: '', visible: true });
  }, [newItem]);

  const handleSectionAvailabilityChange = useCallback((section, available) => {
    const key = section === 'lmr' ? 'lmrAvailable' : 'topicNotesAvailable';
    setUiParams(prev => ({
      ...prev,
      [key]: available
    }));
  }, []);

  const renderItem = useCallback((item, section, index) => {
    const isEditing = editingItem?.section === section && editingItem?.index === index;

    if (isEditing) {
      return (
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 1, mb: 1 }}>
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Key"
              value={newItem.key}
              onChange={(e) => setNewItem(prev => ({ ...prev, key: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="Title"
              value={newItem.title}
              onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="Source"
              value={newItem.source}
              onChange={(e) => setNewItem(prev => ({ ...prev, source: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newItem.visible}
                  onChange={(e) => setNewItem(prev => ({ ...prev, visible: e.target.checked }))}
                />
              }
              label="Visible"
            />
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" onClick={handleItemSave}>
                Save
              </Button>
              <Button size="small" onClick={() => setEditingItem(null)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 1, 
        border: '1px solid', 
        borderColor: 'divider', 
        borderRadius: 1, 
        mb: 0.5,
        backgroundColor: item.visible ? 'action.hover' : 'background.paper',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1
        }
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={item.visible}
              onChange={(e) => handleItemVisibilityChange(section, index, e.target.checked)}
              disabled={disabled}
            />
          }
          label=""
        />
        <Box sx={{ flex: 1, ml: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {item.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Key: {item.key} | Source: {item.source}
          </Typography>
        </Box>
        {!disabled && (
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={() => handleItemEdit(section, index)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleItemDelete(section, index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Box>
    );
  }, [editingItem, newItem, handleItemVisibilityChange, handleItemEdit, handleItemDelete, handleItemSave, disabled]);

  const renderNewItemForm = useCallback((section) => (
    <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'primary.main', borderRadius: 1, mb: 1.5 }}>
      <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
        Add New Item to {section === 'lmr' ? 'LMR' : 'Topic Notes'}
      </Typography>
      <Stack spacing={1.5}>
        <TextField
          size="small"
          label="Key"
          value={newItem.key}
          onChange={(e) => setNewItem(prev => ({ ...prev, key: e.target.value }))}
          placeholder="e.g., new-feature"
        />
        <TextField
          size="small"
          label="Title"
          value={newItem.title}
          onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
          placeholder="e.g., New Feature"
        />
        <TextField
          size="small"
          label="Source"
          value={newItem.source}
          onChange={(e) => setNewItem(prev => ({ ...prev, source: e.target.value }))}
          placeholder="e.g., New Source"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={newItem.visible}
              onChange={(e) => setNewItem(prev => ({ ...prev, visible: e.target.checked }))}
            />
          }
          label="Visible by default"
        />
        <Button 
          size="small" 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleAddItem(section)}
          disabled={!newItem.key || !newItem.title || !newItem.source}
        >
          Add Item
        </Button>
      </Stack>
    </Box>
  ), [newItem, handleAddItem]);

  // Memoize the LMR section
  const lmrSection = useMemo(() => (
    <Accordion 
      expanded={expandedSections.lmr} 
      onChange={() => handleSectionToggle('lmr')}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
            LMR Configuration
          </Typography>
          <Chip 
            label={uiParams.lmrAvailable ? "Available" : "Disabled"} 
            color={uiParams.lmrAvailable ? "success" : "default"}
            size="small"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <FormControlLabel
          control={
            <Checkbox
              checked={uiParams.lmrAvailable}
              onChange={(e) => handleSectionAvailabilityChange('lmr', e.target.checked)}
              disabled={disabled}
            />
          }
          label="LMR Available"
          sx={{ mb: 2 }}
        />
        
        {(uiParams.lmr || []).map((item, index) => 
          renderItem(item, 'lmr', index)
        )}
        
        {!disabled && renderNewItemForm('lmr')}
      </AccordionDetails>
    </Accordion>
  ), [expandedSections.lmr, uiParams.lmrAvailable, uiParams.lmr, handleSectionToggle, handleSectionAvailabilityChange, renderItem, renderNewItemForm, disabled]);

  // Memoize the Topic Notes section
  const topicNotesSection = useMemo(() => (
    <Accordion 
      expanded={expandedSections.topic_notes} 
      onChange={() => handleSectionToggle('topic_notes')}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
            Topic Notes Configuration
          </Typography>
          <Chip 
            label={uiParams.topicNotesAvailable ? "Available" : "Disabled"} 
            color={uiParams.topicNotesAvailable ? "success" : "default"}
            size="small"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <FormControlLabel
          control={
            <Checkbox
              checked={uiParams.topicNotesAvailable}
              onChange={(e) => handleSectionAvailabilityChange('topic_notes', e.target.checked)}
              disabled={disabled}
            />
          }
          label="Topic Notes Available"
          sx={{ mb: 2 }}
        />
        
        {(uiParams.topic_notes || []).map((item, index) => 
          renderItem(item, 'topic_notes', index)
        )}
        
        {!disabled && renderNewItemForm('topic_notes')}
      </AccordionDetails>
    </Accordion>
  ), [expandedSections.topic_notes, uiParams.topicNotesAvailable, uiParams.topic_notes, handleSectionToggle, handleSectionAvailabilityChange, renderItem, renderNewItemForm, disabled]);

  return (
    <Box>
      <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>
        Configure UI Elements
      </Typography>
      
      {lmrSection}
      {topicNotesSection}

      <Divider sx={{ my: 2 }} />
      
      <Typography variant="body2" color="text.secondary">
        This configuration will be automatically saved as JSON when you save the book.
      </Typography>
    </Box>
  );
});

UIParamsConfig.displayName = 'UIParamsConfig';

export default UIParamsConfig;
